import * as React from "react";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import CleaningZone, { isTeachAndRepeatZone } from "../../../interfaces/robot/rooms/cleaning-zone";
import { zonePointsToSvgString } from "../../../utils/map/zone-helper";
import ZoneSvgPolygon from "./ZoneSvgPolygon";

export interface CleaningZoneSelectionOverlayProps {
  roomData: RoomData;
  cleaningZones: CleaningZone[];
  selectedZoneIds: string[];
  onZoneToggle: (zoneId: string) => void;
  zoomScale?: number;
  /** Zone id that should be visually highlighted (e.g. hovered in an external list). */
  hoverZoneId?: string | null;
  /** Called when the pointer enters or leaves a polygon so the parent can sync an external list. */
  onHoverZoneChange?: (id: string | null) => void;
  /** When true, cursor changes to indicate send-to-back mode. */
  isSendToBackMode?: boolean;
}

/**
 * A purely presentational SVG overlay that renders cleaning zone polygons
 * for selection purposes. It has no Redux dependency and accepts all data
 * as props, making it reusable in the zone cleaning dialog (local API data),
 * the zone planner / scheduler (Redux state data), and the missions view.
 */
const CleaningZoneSelectionOverlay: React.FC<CleaningZoneSelectionOverlayProps> = ({
  roomData,
  cleaningZones,
  selectedZoneIds,
  onZoneToggle,
  zoomScale = 1,
  hoverZoneId,
  onHoverZoneChange,
  isSendToBackMode = false,
}) => {
  return (
    <svg
      viewBox={`0 0 ${roomData.mapInformation.width} ${roomData.mapInformation.height}`}
      preserveAspectRatio="xMinYMin meet"
      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
    >
      {cleaningZones.map((zone) => {
        const pointsStr = zonePointsToSvgString(zone.points, roomData);

        return (
          <ZoneSvgPolygon
            key={zone.id}
            points={pointsStr}
            isTnR={isTeachAndRepeatZone(zone)}
            isSelected={selectedZoneIds.includes(zone.id)}
            externalHovered={hoverZoneId === zone.id}
            onHoverChange={(isHovered) => onHoverZoneChange?.(isHovered ? zone.id : null)}
            strokeWidth={2 / zoomScale}
            style={{ cursor: isSendToBackMode ? "cell" : "pointer" }}
            onClick={() => onZoneToggle(zone.id)}
          />
        );
      })}
    </svg>
  );
};

export default CleaningZoneSelectionOverlay;

